import { useEffect, useState } from 'react';

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

export const useCountdown = (targetIso) => {
  const [ms, setMs] = useState(() => (targetIso ? new Date(targetIso) - Date.now() : 0));
  useEffect(() => {
    if (!targetIso) return undefined;
    const tick = () => setMs(new Date(targetIso) - Date.now());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  const total = Math.max(0, Math.floor(ms / 1000));
  return { expired: ms <= 0, days: Math.floor(total / 86400), hours: pad(Math.floor((total % 86400) / 3600)), minutes: pad(Math.floor((total % 3600) / 60)), seconds: pad(total % 60) };
};

export const fmtCT = (iso, opts = {}) =>
  new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...opts }) + ' CT';

export const fmtDay = (ymd) => new Date(`${ymd}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

export const fmtMoney = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const WindowBanner = ({ window: w, onExpire }) => {
  const target = w.is_open ? w.closes_at : w.next_opens_at;
  const c = useCountdown(target);
  useEffect(() => { if (c.expired && onExpire) onExpire(); }, [c.expired, onExpire]);
  return (
    <div
      className={`relative overflow-hidden rounded-3xl p-6 text-white shadow-[0px_18px_40px_rgba(112,144,176,0.18)] ${w.is_open ? 'bg-gradient-to-br from-[#064E3B] via-[#0B1437] to-[#0B1437]' : 'bg-gradient-to-br from-[#0B1437] via-[#111C44] to-[#2A1335]'}`}
      data-testid="lead-store-window-banner"
    >
      <div className={`pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full blur-3xl ${w.is_open ? 'bg-emerald-400/30' : 'bg-rose-400/20'}`} />
      <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider ${w.is_open ? 'bg-emerald-400 text-[#06263A]' : 'bg-white/15 text-white'}`} data-testid="lead-store-window-status">
            <span className={`h-2 w-2 rounded-full ${w.is_open ? 'bg-[#06263A] animate-pulse' : 'bg-rose-400'}`} />
            {w.is_open ? 'Purchasing Open' : 'Purchasing Closed'}
            {w.override ? ' · Admin override' : ''}
          </span>
          <h2 className="mt-3 font-['Manrope'] text-xl font-extrabold md:text-2xl">
            {w.is_open ? 'Lead purchasing is OPEN. Secure your leads before Thursday at 6:00 PM CT.' : 'Lead purchasing is currently closed. The next purchasing window opens Thursday at 8:00 AM CT.'}
          </h2>
          <p className="mt-2 text-sm text-white/60">
            {w.schedule_label} · Next fulfillment begins <span className="font-bold text-white">{fmtDay(w.fulfillment_starts)}</span> afternoon
          </p>
        </div>
        <div className="shrink-0" data-testid="lead-store-countdown">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">{w.is_open ? 'Closes in' : 'Reopens in'}</p>
          <div className="flex items-end gap-2">
            {[['d', c.days], ['h', c.hours], ['m', c.minutes], ['s', c.seconds]].filter(([k, v]) => k !== 'd' || v > 0).map(([k, v]) => (
              <div key={k} className="min-w-[64px] rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-center backdrop-blur-md">
                <p className="font-['Manrope'] text-3xl font-extrabold tabular-nums leading-none">{v}</p>
                <p className="mt-1 text-[10px] font-bold uppercase text-white/50">{{ d: 'days', h: 'hrs', m: 'min', s: 'sec' }[k]}</p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-white/45">{fmtCT(target)}</p>
        </div>
      </div>
    </div>
  );
};
